import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Flex,
  Progress,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { DataSkeleton, EmptyState, ErrorState } from '../states';
import {
  Alert,
  AlertDescription,
  Dialog,
  FramedCard,
  FramedCardContent,
  KpiStrip,
  TabStrip,
} from '../ui';
import { useAnalyticsData } from '../../hooks/useAnalyticsData';
import { useAuth } from '../../providers/auth';

interface AnalyticsModalProps {
  isOpen: boolean;
  onClose: () => void;
  moduleId?: number;
  weekId?: number;
  materialId?: number;
  type: 'module' | 'week' | 'material';
  title?: string;
}

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString('pt-BR') : '-';

const percentTone = (value: number) => {
  if (value >= 70) return 'success';
  if (value > 0) return 'warning';
  return 'neutral';
};

const AccessBar = ({
  accessed,
  total,
  percentage,
}: {
  accessed: number;
  total: number;
  percentage: number;
}) => (
  <Flex align="center" gap="10px" minW="180px">
    <Progress
      value={percentage}
      size="xs"
      flex="1"
      borderRadius="full"
      bg="surface.selected"
      sx={{ '& > div': { bg: percentage >= 70 ? 'state.success' : 'state.warning' } }}
    />
    <Text fontFamily="mono" fontSize="12px" color="content.subtle" whiteSpace="nowrap">
      {accessed}/{total}
    </Text>
    <Badge variant={percentTone(percentage)} minW="48px" justifyContent="center">
      {percentage.toFixed(0)}%
    </Badge>
  </Flex>
);

const UserCell = ({ name, email }: { name: string; email?: string }) => (
  <Flex align="center" gap="10px" minW={0}>
    <Avatar
      size="xs"
      w="24px"
      h="24px"
      name={name}
      bg="surface.selected"
      color="content.body"
      fontSize="10px"
    />
    <Box minW={0}>
      <Text fontSize="13px" lineHeight="18px" color="content.strong" isTruncated>
        {name}
      </Text>
      {email && (
        <Text fontSize="12px" lineHeight="16px" color="content.subtle" isTruncated>
          {email}
        </Text>
      )}
    </Box>
  </Flex>
);

const ScrollTable = ({ children }: { children: React.ReactNode }) => (
  <Box
    maxH="360px"
    overflowY="auto"
    border="1px solid"
    borderColor="line.hairline"
    borderRadius="lg"
  >
    <Table variant="serra" size="sm">
      {children}
    </Table>
  </Box>
);

export const AnalyticsModal: React.FC<AnalyticsModalProps> = ({
  isOpen,
  onClose,
  moduleId,
  weekId,
  materialId,
  type,
  title,
}) => {
  const { user } = useAuth();
  const { data, loading, hasAccess, error, fetchData } = useAnalyticsData(user);
  const [weekTab, setWeekTab] = useState('accessed');

  const getEndpoint = () => {
    if (type === 'module' && moduleId) return `/modules/${moduleId}/weeks`;
    if (type === 'week' && weekId) return `/weeks/${weekId}/meetings`;
    if (type === 'material' && materialId) return `/materials/${materialId}`;
    return '/modules';
  };

  useEffect(() => {
    if (isOpen && hasAccess) {
      setWeekTab('accessed');
      fetchData(getEndpoint());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasAccess]);

  const renderModuleStats = () => {
    if (!data || !data.weeks) return null;
    return (
      <Flex direction="column" gap="16px">
        <FramedCard>
          <FramedCardContent p={0}>
            <KpiStrip
              items={[
                {
                  id: 'weeks',
                  label: 'Semanas',
                  value: data.summary?.total_weeks || 0,
                  hint: 'Neste módulo',
                },
                {
                  id: 'users',
                  label: 'Usuários',
                  value: data.summary?.total_users || 0,
                  hint: 'Professores e tutores',
                },
              ]}
            />
          </FramedCardContent>
        </FramedCard>
        <ScrollTable>
          <Thead position="sticky" top={0} bg="surface.base" zIndex={1}>
            <Tr>
              <Th>Semana</Th>
              <Th>Professores</Th>
              <Th>Tutores</Th>
            </Tr>
          </Thead>
          <Tbody>
            {data.weeks.map((week: any) => (
              <Tr key={week.week_id}>
                <Td>
                  <Text fontWeight={500}>{week.week_name}</Text>
                  {week.week_name !== `Semana ${week.week_number}` && (
                    <Text fontSize="12px" lineHeight="16px" color="content.subtle">
                      Semana {week.week_number}
                    </Text>
                  )}
                </Td>
                <Td>
                  <AccessBar
                    accessed={week.professors.accessed}
                    total={week.professors.total}
                    percentage={week.professors.percentage}
                  />
                </Td>
                <Td>
                  <AccessBar
                    accessed={week.tutors.accessed}
                    total={week.tutors.total}
                    percentage={week.tutors.percentage}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </ScrollTable>
      </Flex>
    );
  };

  const renderWeekStats = () => {
    if (!data) return null;
    const accessedUsers = data.accessed?.users ?? [];
    const notAccessedUsers = data.not_accessed?.users ?? [];
    const rows = weekTab === 'accessed' ? accessedUsers : notAccessedUsers;
    return (
      <Flex direction="column" gap="16px">
        <FramedCard>
          <FramedCardContent p={0}>
            <KpiStrip
              items={[
                {
                  id: 'users',
                  label: 'Usuários',
                  value: data.total_users || 0,
                  hint: 'Elegíveis nesta semana',
                },
                {
                  id: 'accessed',
                  label: 'Acessaram',
                  value: data.accessed?.count || 0,
                  delta: `${(data.accessed?.percentage ?? 0).toFixed(0)}%`,
                  deltaTone: (data.accessed?.percentage ?? 0) >= 70 ? 'positive' : 'neutral',
                  hint: 'Abriram ao menos um encontro',
                },
                {
                  id: 'not_accessed',
                  label: 'Não acessaram',
                  value: data.not_accessed?.count || 0,
                  hint: 'Sem acesso registrado',
                },
              ]}
            />
          </FramedCardContent>
        </FramedCard>
        <FramedCard>
          <FramedCardContent p={0} gap={0}>
            <TabStrip
              activeId={weekTab}
              onChange={setWeekTab}
              items={[
                { id: 'accessed', label: 'Acessaram', count: accessedUsers.length },
                { id: 'not_accessed', label: 'Não acessaram', count: notAccessedUsers.length },
              ]}
            />
            {rows.length === 0 ? (
              <EmptyState
                variant="bare"
                title={weekTab === 'accessed' ? 'Ninguém acessou ainda' : 'Todos acessaram'}
                description={
                  weekTab === 'accessed'
                    ? 'Nenhum usuário abriu os encontros desta semana.'
                    : 'Não há usuários sem acesso nesta semana.'
                }
              />
            ) : (
              <Box maxH="320px" overflowY="auto">
                <Table variant="serra" size="sm">
                  <Thead position="sticky" top={0} bg="surface.base" zIndex={1}>
                    <Tr>
                      <Th>Usuário</Th>
                      <Th>Grupo</Th>
                      {weekTab === 'accessed' && <Th>Último acesso</Th>}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map((userItem: any) => (
                      <Tr key={userItem.id}>
                        <Td>
                          <UserCell name={userItem.name} email={userItem.email} />
                        </Td>
                        <Td>
                          <Badge variant="neutral">{userItem.group_name}</Badge>
                        </Td>
                        {weekTab === 'accessed' && (
                          <Td fontFamily="mono" fontSize="12px" color="content.subtle">
                            {formatDate(userItem.last_access)}
                          </Td>
                        )}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </FramedCardContent>
        </FramedCard>
      </Flex>
    );
  };

  const renderMaterialStats = () => {
    if (!data) return null;
    const summary = data.summary || {};
    const users = data.users || [];
    return (
      <Flex direction="column" gap="16px">
        <FramedCard>
          <FramedCardContent p={0}>
            <KpiStrip
              items={[
                { id: 'views', label: 'Visualizações', value: summary.total_views || 0 },
                { id: 'downloads', label: 'Downloads', value: summary.total_downloads || 0 },
                { id: 'unique', label: 'Usuários únicos', value: summary.unique_users || 0 },
              ]}
            />
          </FramedCardContent>
        </FramedCard>
        {users.length > 0 ? (
          <ScrollTable>
            <Thead position="sticky" top={0} bg="surface.base" zIndex={1}>
              <Tr>
                <Th>Usuário</Th>
                <Th>Grupo</Th>
                <Th isNumeric>Visualizações</Th>
                <Th isNumeric>Downloads</Th>
                <Th>Último acesso</Th>
              </Tr>
            </Thead>
            <Tbody>
              {users.map((userItem: any) => (
                <Tr key={userItem.id}>
                  <Td>
                    <UserCell name={userItem.name} email={userItem.email} />
                  </Td>
                  <Td>
                    <Badge variant="neutral">{userItem.group_name}</Badge>
                  </Td>
                  <Td isNumeric fontFamily="mono" fontSize="12px">
                    {userItem.views}
                  </Td>
                  <Td isNumeric fontFamily="mono" fontSize="12px">
                    {userItem.downloads}
                  </Td>
                  <Td fontFamily="mono" fontSize="12px" color="content.subtle">
                    {formatDate(userItem.last_access)}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </ScrollTable>
        ) : (
          <EmptyState
            variant="bare"
            title="Nenhum acesso registrado"
            description="Este material ainda não possui visualizações ou downloads."
          />
        )}
      </Flex>
    );
  };

  const defaultTitle = {
    module: 'Estatísticas do módulo',
    week: 'Estatísticas da semana',
    material: 'Estatísticas do material',
  }[type];

  const subject = title || data?.material?.name;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      title={subject ? `${defaultTitle} · ${subject}` : defaultTitle}
      description={
        data?.last_updated ? `Atualizado em ${formatDate(data.last_updated)}` : undefined
      }
    >
      {!hasAccess && (
        <Alert variant="warning">
          <AlertDescription>Você não tem permissão para visualizar estatísticas.</AlertDescription>
        </Alert>
      )}
      {hasAccess && loading && (
        <DataSkeleton variant="cards" rows={3} label="Carregando estatísticas" />
      )}
      {hasAccess && !loading && error && <ErrorState onRetry={() => fetchData(getEndpoint())} />}
      {hasAccess && !loading && !error && data && (
        <>
          {type === 'module' && renderModuleStats()}
          {type === 'week' && renderWeekStats()}
          {type === 'material' && renderMaterialStats()}
        </>
      )}
      {hasAccess && !loading && !error && !data && (
        <EmptyState
          variant="bare"
          title="Nenhuma estatística encontrada"
          description="Não existem dados disponíveis para este item."
        />
      )}
    </Dialog>
  );
};
